import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { RichEditor } from "components/RichEditor/RichEditor";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { Select } from "components/ui/Select/Select";
import { useCreatePatientLetter } from "features/communications/hooks/useCreatePatientLetter";
import { useLetterDetail } from "features/communications/hooks/useLetterDetail";
import { useLetterTemplates } from "features/templates/hooks/useLetterTemplates";

import styles from "./AddLetterModal.module.css";

const LETTER_TYPE_OPTIONS = [
  { value: "General", label: "General" },
  { value: "Referral", label: "Referral" },
  { value: "Discharge", label: "Discharge" },
  { value: "Other", label: "Other" },
];

const formSchema = z.object({
  name: z.string().min(1, { message: "Please provide a letter title." }),
  letterType: z.string().min(1, { message: "Please select a type." }),
  text: z.string().optional(),
});

// A distinct component (rather than inline hooks in EditLetterModal) so it
// mounts fresh every time the dialog opens — Dialog omits its children from
// the tree entirely while closed, so this instance (and its RHF state) is
// naturally torn down and rebuilt from the latest `letter` on each open,
// mirroring how AddSickCertForm is remounted per selection.
const LetterEditForm = ({ letter, patientId, onClose, onSaved }) => {
  const { data: templates = [] } = useLetterTemplates(
    patientId,
    letter?.consultationId
  );
  const { mutateAsync: saveLetter } = useCreatePatientLetter();

  const [text, setText] = useState(letter?.text || "");
  const [editorKey, setEditorKey] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: letter?.name || "",
      letterType: letter?.letterType || "",
      text: letter?.text || "",
    },
  });

  const templateOptions = [
    { value: "", label: "Keep current content" },
    ...templates.map((template) => ({
      value: String(template.id),
      label: template.name,
    })),
  ];

  const handleTemplateSelect = (value) => {
    setSelectedTemplateId(value);

    if (!value) return;

    const template = templates.find((t) => String(t.id) === value);

    if (!template) return;

    setText(template.template ?? "");
    form.setValue("text", template.template ?? "", { shouldDirty: true });
    setEditorKey((previous) => previous + 1);
  };

  const handleSave = async () => {
    const isValid = await form.trigger();

    if (!isValid) return;

    setSaveError(null);
    setIsSaving(true);

    try {
      const values = form.getValues();
      // Spread the original record first so id/documentId/consultationId
      // ride along unchanged — the backend UPDATE sets those columns from
      // whatever is sent, so omitting documentId would null out the link to
      // the existing PDF and make every save mint a new, orphaned document
      // instead of regenerating the one already attached to this letter.
      await saveLetter({
        ...letter,
        name: values.name,
        letterType: values.letterType,
        text: values.text,
      });
      onSaved();
    } catch {
      setSaveError("Failed to save letter. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormProvider {...form}>
      <div className={styles.content}>
        <div className={styles.formRow}>
          <FormField name="name" label="Letter title *">
            <input
              className={styles.textInput}
              placeholder="Enter letter title"
              {...form.register("name")}
            />
          </FormField>

          <FormField name="letterType" label="Type *">
            <Select
              options={LETTER_TYPE_OPTIONS}
              value={form.watch("letterType")}
              onChange={(value) =>
                form.setValue("letterType", value, { shouldDirty: true })
              }
              fullWidth
            />
          </FormField>
        </div>

        <div className={styles.templateRow}>
          <label className={styles.templateLabel}>Replace with template</label>
          <Select
            options={templateOptions}
            value={selectedTemplateId}
            onChange={handleTemplateSelect}
            fullWidth
          />
        </div>

        <FormField name="text" label="Content">
          <RichEditor
            key={editorKey}
            editorValue={text}
            onChange={(value) => {
              setText(value);
              form.setValue("text", value, { shouldDirty: true });
            }}
          />
        </FormField>

        <div className={styles.footer}>
          <span className={styles.footerHint}>PDF regenerated on save</span>

          <div className={styles.footerActions}>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>

            <Button variant="primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>

        {saveError && <div className={styles.errorText}>{saveError}</div>}
      </div>
    </FormProvider>
  );
};

export const EditLetterModal = ({
  open,
  letterId,
  patientId,
  onClose,
  refetchList,
}) => {
  const { data: letter, isLoading, refetch: refetchDetail } =
    useLetterDetail(letterId);

  const handleSaved = () => {
    refetchList?.();
    refetchDetail();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={letter?.name || "Letter"}
      width={860}
      minHeight={600}
    >
      {isLoading ? (
        <p className={styles.loading}>Loading…</p>
      ) : letter ? (
        <LetterEditForm
          letter={letter}
          patientId={patientId}
          onClose={onClose}
          onSaved={handleSaved}
        />
      ) : (
        <p className={styles.loading}>Letter not found.</p>
      )}
    </Dialog>
  );
};
